import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import CourseTab from './CourseTab';
import MyCourseTab from './MyCourseTab';
import UserCertificates from '../certificates/Certificates';

function UserCourses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    tabParam === 'browse' ? 'courses' : tabParam === 'certificates' ? 'certificates' : 'my-courses'
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const searchTimeout = useRef(null);

  useEffect(() => {
    if (tabParam === 'browse') setActiveTab('courses');
    else if (tabParam === 'certificates') setActiveTab('certificates');
    else if (tabParam === 'my-courses' || tabParam == null) setActiveTab('my-courses');
  }, [tabParam]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setSearchTerm(value), 400);
  };

  return (
    <div className="courses-page min-h-[60vh]">


      {/* Tab switcher + Search bar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
        {/* Mobile tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 lg:hidden">
          <button
            type="button"
            onClick={() => { setActiveTab('my-courses'); setSearchParams({}); }}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              activeTab === 'my-courses'
                ? 'bg-[var(--primary-color)] text-white shadow-md'
                : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)]'
            }`}
          >
            My Courses
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('courses'); setSearchParams({ tab: 'browse' }); }}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              activeTab === 'courses'
                ? 'bg-[var(--primary-color)] text-white shadow-md'
                : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)]'
            }`}
          >
            Explore
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('certificates'); setSearchParams({ tab: 'certificates' }); }}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              activeTab === 'certificates'
                ? 'bg-[var(--primary-color)] text-white shadow-md'
                : 'bg-[var(--bg-secondary)] text-[var(--text-secondary)]'
            }`}
          >
            Certificates
          </button>
        </div>

        {/* Desktop tab cards */}
        <div className="hidden lg:grid grid-cols-3 gap-3 flex-1">
          <button
            type="button"
            onClick={() => { setActiveTab('my-courses'); setSearchParams({}); }}
            className={`courses-tab-card flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all duration-300 ${
              activeTab === 'my-courses'
                ? 'border-[var(--primary-color)] bg-[var(--primary-color)]/10 shadow-lg shadow-[var(--primary-color)]/10'
                : 'border-[var(--border-color)] bg-[var(--bg-primary)] hover:border-[var(--primary-lighter)] hover:bg-[var(--primary-color)]/5'
            }`}
          >
            <div
              className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                activeTab === 'my-courses' ? 'bg-[var(--primary-color)] text-white' : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)]'
              }`}
            >
              <Icon icon="fluent:library-24-filled" width={22} height={22} />
            </div>
            <div className="min-w-0">
              <span className={`block text-sm font-semibold ${activeTab === 'my-courses' ? 'text-[var(--primary-color)]' : 'text-[var(--text-primary)]'}`}>
                My Courses
              </span>
              <span className="block text-xs text-[var(--text-secondary)] mt-0.5">Your enrolled courses</span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => { setActiveTab('courses'); setSearchParams({ tab: 'browse' }); }}
            className={`courses-tab-card flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all duration-300 ${
              activeTab === 'courses'
                ? 'border-[var(--primary-color)] bg-[var(--primary-color)]/10 shadow-lg shadow-[var(--primary-color)]/10'
                : 'border-[var(--border-color)] bg-[var(--bg-primary)] hover:border-[var(--primary-lighter)] hover:bg-[var(--primary-color)]/5'
            }`}
          >
            <div
              className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                activeTab === 'courses' ? 'bg-[var(--primary-color)] text-white' : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)]'
              }`}
            >
              <Icon icon="fluent:compass-northwest-24-filled" width={22} height={22} />
            </div>
            <div className="min-w-0">
              <span className={`block text-sm font-semibold ${activeTab === 'courses' ? 'text-[var(--primary-color)]' : 'text-[var(--text-primary)]'}`}>
                Explore Courses
              </span>
              <span className="block text-xs text-[var(--text-secondary)] mt-0.5">Browse and enroll in new courses</span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => { setActiveTab('certificates'); setSearchParams({ tab: 'certificates' }); }}
            className={`courses-tab-card flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all duration-300 ${
              activeTab === 'certificates'
                ? 'border-[var(--primary-color)] bg-[var(--primary-color)]/10 shadow-lg shadow-[var(--primary-color)]/10'
                : 'border-[var(--border-color)] bg-[var(--bg-primary)] hover:border-[var(--primary-lighter)] hover:bg-[var(--primary-color)]/5'
            }`}
          >
            <div
              className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                activeTab === 'certificates' ? 'bg-[var(--primary-color)] text-white' : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)]'
              }`}
            >
              <Icon icon="fluent:certificate-24-filled" width={22} height={22} />
            </div>
            <div className="min-w-0">
              <span className={`block text-sm font-semibold ${activeTab === 'certificates' ? 'text-[var(--primary-color)]' : 'text-[var(--text-primary)]'}`}>
                Certificates
              </span>
              <span className="block text-xs text-[var(--text-secondary)] mt-0.5">View and download your certificates</span>
            </div>
          </button>
        </div>

        {/* Search bar - visible on both my-courses and browse tabs */}
        {(activeTab === 'my-courses' || activeTab === 'courses') && (
          <div className="lg:w-96 flex-shrink-0">
            <div className="relative">
              <Icon
                icon="mdi:magnify"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] pointer-events-none"
                width={20}
                height={20}
                aria-hidden
              />
              <input
                id="course-search"
                type="search"
                autoComplete="off"
                className="w-full pl-12 pr-4 py-3 text-sm rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]/20 focus:border-[var(--primary-color)] transition-all"
                placeholder={activeTab === 'my-courses' ? 'Search your courses...' : 'Search courses...'}
                value={inputValue}
                onChange={handleSearchChange}
                aria-label="Search courses"
              />
              {isSearchLoading && (
                <span
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-[var(--primary-color)] border-t-transparent rounded-full animate-spin"
                  role="status"
                  aria-label="Loading"
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tab content */}
      <div className="tab-content">
        {activeTab === 'my-courses' && (
          <div className="animate-fade-in">
            <MyCourseTab searchTerm={searchTerm} setIsSearchLoading={setIsSearchLoading} />
          </div>
        )}
        {activeTab === 'courses' && (
          <div className="animate-fade-in">
            <CourseTab searchTerm={searchTerm} setIsSearchLoading={setIsSearchLoading} />
          </div>
        )}
        {activeTab === 'certificates' && (
          <div className="animate-fade-in">
            <UserCertificates />
          </div>
        )}
      </div>
    </div>
  );
}

export default UserCourses;
